import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IApplicationState } from '../../types/redux';
import { setOrderField, setDealType, setFavoriteEntityType } from '../../actions/filters';
import { fetchFavoriteEntities } from '../../actions/favoriteEntities';
import { EEntityType, EDealType, EOrderField } from '../../types/filters';
import { DealTypeFilterIcons } from '../../components/DealTypeFilterIcons';
import { DealTypeSortModal } from '../../components/DealTypeSortModal';
import { DealTypeFiltersModal } from '../../components/DealTypeFiltersModal';
import { trackShowFilters, trackApplyFilters, trackSortOrder } from './tracking';

export const DealTypeFilterContainer = () => {
  const orderField = useSelector((state: IApplicationState) => state.filters.orderField);
  const dealType = useSelector((state: IApplicationState) => state.filters.dealType);
  const favoriteEntityType = useSelector((state: IApplicationState) => state.filters.favoriteEntityType);
  const [openSortModal, setOpenSortModal] = React.useState<boolean>(false);
  const [openFiltersModal, setOpenFiltersModal] = React.useState<boolean>(false);

  const dispatch = useDispatch();

  const setFilterOrder = React.useCallback(
    (order: EOrderField) => {
      trackSortOrder(order);
      dispatch(setOrderField(order));
      dispatch(fetchFavoriteEntities());
    },
    [trackSortOrder, setOrderField, fetchFavoriteEntities],
  );

  const setFavoriteDealType = React.useCallback(
    (dealType: EDealType | null) => {
      dispatch(setDealType(dealType));
    },
    [setDealType],
  );

  const setEntityType = React.useCallback(
    (entityType: EEntityType | null) => {
      dispatch(setFavoriteEntityType(entityType));
    },
    [setFavoriteEntityType],
  );

  const acceptFilter = React.useCallback(() => {
    trackApplyFilters(dealType, favoriteEntityType);
    setOpenFiltersModal(false);
    dispatch(setDealType(dealType));
    dispatch(setFavoriteEntityType(favoriteEntityType));
    dispatch(fetchFavoriteEntities());
  }, [
    dealType,
    favoriteEntityType,
    trackApplyFilters,
    setOpenFiltersModal,
    setDealType,
    setFavoriteEntityType,
    fetchFavoriteEntities,
  ]);

  const resetFilter = React.useCallback(() => {
    setOpenFiltersModal(false);
    dispatch(fetchFavoriteEntities());
    dispatch(setDealType(null));
    dispatch(setFavoriteEntityType(null));
  }, [setOpenFiltersModal, setDealType, setFavoriteEntityType, fetchFavoriteEntities]);

  const openFilters = React.useCallback(() => {
    trackShowFilters();
    setOpenFiltersModal(true);
  }, [trackShowFilters, setOpenFiltersModal]);

  const openSort = React.useCallback(() => {
    setOpenSortModal(true);
  }, [setOpenSortModal]);

  const closeFilter = React.useCallback(() => {
    setOpenFiltersModal(false);
  }, [setOpenFiltersModal]);

  const closeSort = React.useCallback(() => {
    setOpenSortModal(false);
  }, [setOpenSortModal]);

  return (
    <>
      <DealTypeFilterIcons openFilters={openFilters} openSort={openSort} entityType={favoriteEntityType} />
      <DealTypeSortModal
        orderField={orderField}
        open={openSortModal}
        setFilterOrder={setFilterOrder}
        closeSort={closeSort}
      />
      <DealTypeFiltersModal
        open={openFiltersModal}
        favoriteEntityType={favoriteEntityType}
        dealType={dealType}
        setDealType={setFavoriteDealType}
        setEntityType={setEntityType}
        closeFilter={closeFilter}
        accept={acceptFilter}
        reset={resetFilter}
      />
    </>
  );
};
